// ============================================
// Clinical Capture Strip — Signal Extraction
// Parses patient messages to extract clinical
// signals as pills for the Capture Strip UI.
// ============================================

import { geminiGenerate } from '../ai/providers/gemini';
import type { CapturedSignal, CaptureCategory, CaptureConfig } from '../../types/fashionPlate';

const MODEL = 'gemini-2.5-flash';
const TEMPERATURE = 0.2;
const MAX_TOKENS = 512;

const SYSTEM_PROMPT = `You are a clinical signal extractor for a health intake conversation.
Your job is to identify health-relevant signals from what a patient says.

Categories:
- symptom: Physical or mental symptoms (fatigue, headaches, anxiety, insomnia, etc.)
- medication: Any drugs, supplements, or treatments mentioned (SSRI, vitamin D, metformin, etc.)
- life_event: Significant life events affecting health (divorce, job loss, bereavement, move, etc.)
- context: Lifestyle and environmental factors (diet, exercise, sleep schedule, work stress, etc.)
- history: Past medical history, family history, prior diagnoses (thyroid issue 5 years ago, family diabetes, etc.)

Rules:
- Extract only clearly health-relevant signals from the patient's message.
- Each signal should be a concise term (1-4 words).
- Assign a confidence score from 0 to 1 based on how explicitly the patient stated it.
- If a signal likely correlates with a common biomarker, include linkedBiomarker (e.g., "fatigue" -> "Ferritin", "weight gain" -> "TSH").
- Do NOT extract signals that are already in the existing list.
- Return a JSON array only. No markdown, no explanation.

Output format:
[{ "term": "...", "category": "symptom|medication|life_event|context|history", "confidence": 0.0-1.0, "linkedBiomarker": "..." or null }]

If no new signals are found, return an empty array: []`;

/**
 * Extract clinical signals from a patient message using a single Gemini call.
 * Cost: ~$0.001 per call with gemini-2.5-flash.
 */
export async function extractSignals(
  patientMessage: string,
  turnNumber: number,
  existingSignals: CapturedSignal[],
): Promise<CapturedSignal[]> {
  const existingTerms = existingSignals.map((s) => s.term.toLowerCase());
  const existingList =
    existingTerms.length > 0
      ? `\nAlready captured signals (do NOT re-extract these): ${existingTerms.join(', ')}`
      : '';

  const userMessage = `${existingList}\n\nPatient message:\n"${patientMessage}"`;

  let raw: string;
  try {
    raw = await geminiGenerate(MODEL, SYSTEM_PROMPT, userMessage, TEMPERATURE, MAX_TOKENS);
  } catch {
    return [];
  }

  // Strip markdown fences if Gemini wraps the response
  let cleaned = raw.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\s*/, '').replace(/```\s*$/, '').trim();
  }

  let parsed: Array<{
    term: string;
    category: CaptureCategory;
    confidence: number;
    linkedBiomarker?: string | null;
  }>;

  try {
    parsed = JSON.parse(cleaned);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) return [];

  const validCategories = new Set<string>(['symptom', 'medication', 'life_event', 'context', 'history']);

  return parsed
    .filter(
      (item) =>
        item &&
        typeof item.term === 'string' &&
        typeof item.confidence === 'number' &&
        item.confidence >= 0.5 &&
        validCategories.has(item.category) &&
        !existingTerms.includes(item.term.toLowerCase()),
    )
    .map((item, index) => ({
      id: `sig_${turnNumber}_${index}_${Date.now().toString(36)}`,
      term: item.term,
      category: item.category as CaptureCategory,
      source: 'patient_mention' as const,
      confidence: item.confidence,
      detectedAtTurn: turnNumber,
      linkedBiomarker: item.linkedBiomarker ?? undefined,
      reinforcedAtTurns: [],
    }));
}

/**
 * Build a prompt fragment for nurse reinforcement of captured signals.
 * Pure function — no AI call. Returns '' when reinforcement is not due.
 */
export function buildCaptureReinforcement(
  signals: CapturedSignal[],
  config: CaptureConfig,
  turnNumber: number,
): string {
  if (signals.length === 0) return '';

  const isReinforcementDue = turnNumber % config.reinforcementFrequency === 0;
  const isConversionReached = signals.length >= config.conversionThreshold;

  // Minimal style only fires at conversion threshold
  if (config.style === 'minimal' && !isConversionReached) return '';

  // Other styles need either reinforcement or conversion to be due
  if (!isReinforcementDue && !isConversionReached) return '';

  // Take the most recent 5 signals
  const recentSignals = signals.slice(-5);

  const signalList = recentSignals
    .map((s) => {
      let entry = `- ${s.term} (${s.category})`;
      if (s.linkedBiomarker) entry += ` [linked: ${s.linkedBiomarker}]`;
      return entry;
    })
    .join('\n');

  let fragment = '';

  if (config.style === 'clinical') {
    fragment = `Reference these clinical signals by name and connect to lab correlations:\n${signalList}`;
  } else if (config.style === 'conversational') {
    fragment = `Naturally acknowledge these patient concerns in your response:\n${signalList}`;
  } else {
    // minimal — only fires at conversion threshold (guarded above)
    fragment = `The patient has shared ${signals.length} clinical signals. You may briefly note that their concerns are being captured.`;
  }

  if (isConversionReached) {
    fragment += '\nConsider offering to save their progress.';
  }

  return fragment;
}
